import { type ReactNode } from 'react';

// Renderer markdown → elementi React. INERTE per costruzione: non produce MAI HTML grezzo
// (nessun dangerouslySetInnerHTML), solo nodi di testo ed elementi noti — soddisfa il charter
// ("render CC output inert"). Copre il sottoinsieme che il CLI di Claude Code usa davvero:
// blocchi di codice ```…```, `code` inline, **bold**, *italic*, heading, liste, blockquote.

function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) nodes.push(<code key={`${keyBase}-${i}`} className="md-code">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('**') || tok.startsWith('__')) nodes.push(<strong key={`${keyBase}-${i}`}>{tok.slice(2, -2)}</strong>);
    else nodes.push(<em key={`${keyBase}-${i}`}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ text }: { text: string }): ReactNode {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    // blocco di codice ```…```
    if (/^```/.test(line)) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++; // salta la chiusura
      blocks.push(<pre key={`b${k++}`} className="md-pre"><code>{buf.join('\n')}</code></pre>);
      continue;
    }
    // heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push(<div key={`b${k++}`} className={`md-h md-h${h[1].length}`}>{renderInline(h[2], `h${k}`)}</div>); i++; continue; }
    // lista (righe consecutive)
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*([-*+]|\d+\.)\s+/, '');
        items.push(<li key={`li${k}-${items.length}`}>{renderInline(content, `li${k}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(<ul key={`b${k++}`} className="md-ul">{items}</ul>);
      continue;
    }
    // blockquote
    if (/^>\s?/.test(line)) { blocks.push(<div key={`b${k++}`} className="md-quote">{renderInline(line.replace(/^>\s?/, ''), `q${k}`)}</div>); i++; continue; }
    // riga vuota → separatore
    if (line.trim() === '') { i++; continue; }
    // paragrafo (accorpa fino a vuota / fence / heading / lista / quote)
    const para: string[] = [];
    while (
      i < lines.length && lines[i].trim() !== '' && !/^```/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) && !/^\s*([-*+]|\d+\.)\s+/.test(lines[i]) && !/^>\s?/.test(lines[i])
    ) { para.push(lines[i]); i++; }
    blocks.push(<p key={`b${k++}`} className="md-p">{renderInline(para.join('\n'), `p${k}`)}</p>);
  }
  return <>{blocks}</>;
}
